import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  IconButton,
  Box,
  Alert,
} from "@mui/material";
import { Close } from "@mui/icons-material";

const StructuredOutputDialog = ({ open, onClose, onSave, initialValue = "" }) => {
  const [jsonValue, setJsonValue] = useState(initialValue);
  const [error, setError] = useState("");
  const [isValid, setIsValid] = useState(false);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setJsonValue(initialValue);
      setError("");
      setIsValid(false);
    }
  }, [open, initialValue]);

  const sampleJson = `{
  "response_type": "customer_support",
  "status": "resolved",
  "customer_info": {
    "id": "12345",
    "name": "John Doe",
    "email": "john.doe@example.com"
  },
  "issue": {
    "category": "technical",
    "priority": "high",
    "description": "Login authentication error",
    "resolution": "Password reset completed"
  },
  "actions_taken": [
    "Verified user identity",
    "Reset password",
    "Sent confirmation email"
  ],
  "follow_up_required": false,
  "satisfaction_score": 5
}`;

  const handleJsonChange = (value) => {
    setJsonValue(value);
    
    // Clear previous states
    setError("");
    setIsValid(false);
    
    // Only validate if there's content
    if (value.trim()) {
      try {
        JSON.parse(value);
        setError("");
        setIsValid(true);
      } catch (e) {
        setError(`Invalid JSON: ${e.message}`);
        setIsValid(false);
      }
    }
  };

  const validateJson = () => {
    if (!jsonValue.trim()) {
      setError("Please enter JSON content");
      setIsValid(false);
      return false;
    }

    try {
      JSON.parse(jsonValue);
      setError("");
      setIsValid(true);
      return true;
    } catch (e) {
      setError(`Invalid JSON: ${e.message}`);
      setIsValid(false);
      return false;
    }
  };

  const handleSeeExample = () => {
    setJsonValue(sampleJson);
    setError("");
    setIsValid(false);
  };

  const handleSave = () => {
    if (validateJson()) {
      onSave(jsonValue);
      onClose();
    }
  };

  const handleClose = () => {
    setJsonValue("");
    setError("");
    setIsValid(false);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { 
          height: "auto",
          minHeight: "400px",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column"
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography variant="h6" component="span">
              Structured Output (JSON)
            </Typography>
            <Button
              size="small"
              onClick={handleSeeExample}
              sx={{ 
                ml: 2, 
                textTransform: "none",
                color: "primary.main",
                "&:hover": {
                  backgroundColor: "transparent",
                  textDecoration: "underline"
                }
              }}
            >
              See sample
            </Button>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Requires JSON schema format. Clicking on "Fix using AI" helps in converting to desired format.
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ display: "flex", flexDirection: "column", pt: 2, pb: 1, overflow: "visible", flex: 1 }}>
        <TextField
          placeholder="Provide an example of structured output. The agent will respond in the given structured format."
          value={jsonValue}
          onChange={(e) => handleJsonChange(e.target.value)}
          multiline
          variant="outlined"
          fullWidth
          sx={{ 
            "& .MuiInputBase-root": {
              alignItems: "flex-start"
            },
            "& .MuiInputBase-input": {
              fontFamily: "Monaco, 'Lucida Console', monospace",
              fontSize: "13px",
              lineHeight: 1.4,
              resize: "vertical !important",
              minHeight: "200px !important",
              maxHeight: "50vh !important",
              overflow: "auto !important",
              paddingBottom: "20px !important"
            }
          }}
          InputProps={{
            sx: {
              backgroundColor: "action.hover",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: error ? "error.main" : "divider"
              }
            }
          }}
          inputProps={{
            style: { 
              resize: 'vertical',
              overflow: 'auto'
            }
          }}
        />
        
        {error && (
          <Alert severity="error" sx={{ mt: 1, mb: 0, flexShrink: 0 }}>
            {error}
          </Alert>
        )}
        
        {isValid && !error && (
          <Alert severity="success" sx={{ mt: 1, mb: 0, flexShrink: 0 }}>
            Valid JSON format
          </Alert>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2, gap: 1, mt: 1 }}>
        <Button 
          onClick={validateJson}
          variant="outlined"
          disabled={!jsonValue.trim()}
        >
          Validate
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={handleClose}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          disabled={!jsonValue.trim()}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StructuredOutputDialog;